import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';
import {
  buildPatientSubmissionData,
  PatientCreationFormFields,
} from '../utils/create-patient-helpers';
import { useMedplumSearch } from './useMedplumSearch';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

export function useHandlePatientSubmit() {
  const medplum = useMedplum();
  const { t } = useTranslation('patient-onboarding');
  const { findPersonByAmka } = useMedplumSearch();

  const handlePatientSubmit = useCallback(
    async (
      values: PatientCreationFormFields,
      patient?: Patient | null
    ): Promise<Patient> => {
      // Check if AMKA is provided
      if (values.amka) {
        // Check if this AMKA already exists as a patient
        const existingAmkaPatient = await findPersonByAmka(values.amka);

        if (existingAmkaPatient) {
          // For CREATE mode: if patient is null/undefined and AMKA exists, prevent creation
          if (!patient) {
            notifications.show({
              color: 'red',
              title: t('patient.errors.amkaAlreadyExistsTitle', {
                ns: 'patient-onboarding',
                defaultValue: 'Existing AMKA.',
              }),
              message: t('patient.errors.amkaAlreadyExists', {
                ns: 'patient-onboarding',
                defaultValue:
                  'A patient with this AMKA already exists. Please use a different AMKA or search for the existing patient.',
              }),
            });
            throw new Error('AMKA_ALREADY_EXISTS');
          }

          // For EDIT mode: if AMKA exists and belongs to a different patient, prevent update
          if (existingAmkaPatient.id !== patient.id) {
            notifications.show({
              color: 'red',
              title: t('patient.errors.amkaAlreadyExistsTitle', {
                ns: 'patient-onboarding',
                defaultValue: 'Existing AMKA.',
              }),
              message: t('patient.errors.amkaBelongsToAnotherPatient', {
                ns: 'patient-onboarding',
                defaultValue:
                  'This AMKA belongs to another patient. You cannot assign this AMKA to a different patient.',
              }),
            });
            throw new Error('AMKA_BELONGS_TO_ANOTHER_PATIENT');
          }
        }
      }

      // Build patient submission data
      const patientSubmissionData = await buildPatientSubmissionData(values);

      // If patient exists, we are in edit mode, so update the resource
      if (patient) {
        return medplum.updateResource({
          ...patient,
          ...patientSubmissionData,
        });
      } else {
        // Otherwise, create a new patient
        return medplum.createResource({
          resourceType: 'Patient',
          ...patientSubmissionData,
        });
      }
    },
    [medplum, findPersonByAmka, t]
  );

  return { handlePatientSubmit };
}
